import axios from "axios";
import store from "@/state/store";
import router from "@/router/index";
import { notifyError, openModalError } from "@/state/helpers";
import { getInitialLanguage, getStoredLanguage } from "@/i18n/storage";
import i18n from "@/i18n/i18n";

const checkErrorNetowrk = (/*error*/) => {
  //TODO enviar log remoto
  //console.log(error)
  /*if (error.code === 'ERR_NETWORK') {
        openModalError('ERR_NETWORK')
        return true
    }
    return error.code === 'ERR_NETWORK'*/
  return false;
};

// In-flight GET de-duplication.
//
// Sibling components routinely mount in the same tick and each fire the same
// GET (filter option lists, dashboard counters). Without this, every component
// opens its own request, multiplying load on the API and on Redis, which does a
// token lookup per authenticated request. The burst of simultaneous requests is
// what precedes the RedisTimeoutException spikes in production.
//
// Only requests that overlap in time are merged: an entry is removed as soon as
// it settles, so a later call always hits the network again and this never
// serves stale data.
const inFlightGets = new Map();

class ApiRest {
  constructor() {}

  getClient(
    timeout = 10000,
    addInterceptor401 = true,
    responseType = "",
    contentType = "application/json"
  ) {
    var client = axios.create({
      baseURL: process.env.VUE_APP_APIURL,
      timeout: timeout,
      responseType: responseType,
      headers: {
        "Content-Type": contentType,
        "Accept-Language": getStoredLanguage() || getInitialLanguage(),
      },
    });

    if (addInterceptor401) {
      client.interceptors.response.use(
        (response) => response,
        (error) => {
          var hasNetworkError = checkErrorNetowrk(error);
          if (
            !hasNetworkError &&
            error.response &&
            error.response.status === 401
          ) {
            if (router.currentRoute.value.meta.authRequired) {
              openModalError("NOT_AUTHORIZED");
            }
          } else if (
            !hasNetworkError &&
            error.response &&
            error.response.status === 403
          ) {
            // Forbidden usually means the user is authenticated but lacks permission.
            // Don't block the whole app with the NOT_AUTHORIZED modal.
            const authHeader =
              error?.config?.headers?.Authorization ||
              error?.config?.headers?.authorization ||
              "";
            const bearerValue =
              typeof authHeader === "string"
                ? authHeader.replace(/^Bearer\s+/i, "").trim()
                : "";

            // Some backends return 403 even when auth is missing/expired.
            // If we didn't send a usable token, treat it as unauthenticated.
            if (!bearerValue) {
              if (router.currentRoute.value.meta.authRequired) {
                openModalError("NOT_AUTHORIZED");
              }
              return Promise.reject(error);
            }

            const msg =
              error?.response?.data?.message ||
              error?.response?.data?.Message ||
              i18n.global.t("features.shared.errors.noPermission");
            notifyError(msg);
          } else if (
            !hasNetworkError &&
            error.response &&
            error.response.status === 404
          ) {
            openModalError("NOT_FOUND");
          } else {
            if (!hasNetworkError && !error.response && error.message) {
              if (
                    error.message.toLowerCase() == "network error" 
                    || error.code?.toLowerCase() == "err_network" 
                    || (error?.response?.status ?? 0) >= 500
                ) {
                const log = {
                  url: error?.config?.url,
                  method: error?.config?.method,
                  requestHeaders: error?.config?.headers,
                  requestData: error?.config?.data,
                  responseHeaders: error?.response?.headers,
                  responseData: error?.response?.data,
                  timestamp: new Date().toISOString(),
                  errorMessage: error?.message,
                };
                armazenarLog(log);
              } else {
                notifyError(error.message);
              }
            }
          }
          return Promise.reject(error);
        }
      );
    } else {
      client.interceptors.response.use(
        (response) => response,
        (error) => {
          checkErrorNetowrk(error);
          return Promise.reject(error);
        }
      );
    }
    return client;
  }

  async post(
    path,
    params,
    reqAuth = true,
    headers = [],
    timeout = 180000,
    responseType = "",
    contentType = "application/json"
  ) {
    if (reqAuth) {
      return this.getClient(timeout, true, responseType, contentType).post(
        path,
        params ?? {},
        this.getToken(headers)
      );
    } else {
      return this.getClient(timeout, false, responseType, contentType).post(
        path,
        params ?? {}
      );
    }
  }

  async put(path, params, reqAuth = true, timeout = 180000, responseType = "") {
    if (reqAuth) {
      return this.getClient(timeout, true, responseType).put(
        path,
        params ?? {},
        this.getToken()
      );
    } else {
      return this.getClient(timeout, false, responseType).put(
        path,
        params ?? {}
      );
    }
  }

  async delete(
    path,
    params,
    body = null,
    reqAuth = true,
    timeout = 180000,
    responseType = ""
  ) {
    var queryString = params
      ? Object.keys(params)
          .map(function (key) {
            return key + "=" + params[key];
          })
          .join("&")
      : "";
    if (reqAuth) {
      return this.getClient(timeout, true, responseType).delete(
        `${path}?${queryString}`,
        { ...this.getToken(), data: body ?? {} }
      );
    } else {
      return this.getClient(timeout, false, responseType).delete(
        `${path}?${queryString}`,
        { data: body ?? {} }
      );
    }
  }

  async patch(
    path,
    params,
    reqAuth = true,
    timeout = 180000,
    responseType = ""
  ) {
    if (reqAuth) {
      return this.getClient(timeout, true, responseType).patch(
        path,
        params ?? {},
        this.getToken()
      );
    } else {
      return this.getClient(timeout, false, responseType).patch(
        path,
        params ?? {}
      );
    }
  }


  getParamValue(vl) {
    return vl === null || vl === undefined ? "" : vl;
  }
  getArrayToQueryString(array, fieldName, parentKey) {
    if (array.length > 0) {
      let result = "";
      array.forEach((element, index) => {
        if (typeof element === "string" || element instanceof String) {
          result += `${parentKey}${fieldName}[${index}]=${element}&`;
        } else {
          const keys = Object.keys(element);
          if (keys.length === 0) {
            result += `${parentKey}${fieldName}[${index}]=${this.getParamValue(
              element
            )}&`;
          } else {
            result +=
              keys
                .map((key) => {
                  if (Array.isArray(element[key])) {
                    return this.getArrayToQueryString(
                      element[key],
                      key,
                      `${parentKey}${fieldName}[${index}].`
                    );
                  } else {
                    if (isNaN(key)) {
                      return `${parentKey}${fieldName}[${index}].${key}=${this.getParamValue(
                        element[key]
                      )}`;
                    } else {
                      return `${parentKey}${fieldName}[${index}]=${this.getParamValue(
                        element[key]
                      )}`;
                    }
                  }
                })
                .join("&") + "&";
          }
        }
      });
      return result;
    }
    return fieldName + "=";
  }

  getQueryStringParam(object, key) {
    if (Array.isArray(object[key])) {
      return this.getArrayToQueryString(object[key], key, "");
    } else {
      return key + "=" + this.getParamValue(object[key]);
    }
  }
  async get(path, params, reqAuth = true, timeout = 180000, responseType = "") {
    var queryString = "";
    if (params) {
      queryString = Object.keys(params)
        .filter((key) => {
          // exclude empty/null/undefined params from query string to avoid server-side null handling issues
          const v = this.getParamValue(params[key]);
          return v !== "";
        })
        .map((key) => this.getQueryStringParam(params, key))
        .join("&");
    }
    const url = queryString ? `${path}?${queryString}` : path;

    const dedupeKey = `${reqAuth ? "auth" : "anon"}:${responseType || "json"}:${url}`;
    const pending = inFlightGets.get(dedupeKey);
    if (pending) return pending;

    const request = (
      reqAuth
        ? this.getClient(timeout, true, responseType).get(url, this.getToken())
        : this.getClient(timeout, false, responseType).get(url)
    ).finally(() => {
      inFlightGets.delete(dedupeKey);
    });

    inFlightGets.set(dedupeKey, request);
    return request;
  }

  getToken(headers = []) {
    const tokenFromStore = store.getters["account/token"];
    const tokenFromLocalStorage = localStorage.getItem("mention.token");
    let token = tokenFromStore || tokenFromLocalStorage || "";

    if (typeof token === "string") {
      token = token.trim();
      // Guard against accidentally stored quoted tokens
      if (
        (token.startsWith('"') && token.endsWith('"')) ||
        (token.startsWith("'") && token.endsWith("'"))
      ) {
        token = token.slice(1, -1).trim();
      }
    }

    // Normalize common bad values
    if (token === "null" || token === "undefined") token = "";

    const authHeaders = {};
    if (token) {
      authHeaders.Authorization = "Bearer " + token;
    }

    var r = { headers: authHeaders };
    for (var idx = 0; idx < headers.length; idx++) {
      r.headers[headers[idx].name] = headers[idx].value;
    }
    return r;
  }
}

import ApiRestFake from "./api-fake";
import { armazenarLog } from "./logs-service";
const apiClient =
  process.env.VUE_APP_APIENV === "fake" ? new ApiRestFake() : new ApiRest();

export default apiClient;
